import {
  ScrollView,
  StyleSheet,
  Pressable,
  View,
  Text,
  Keyboard,
  useWindowDimensions,
  Platform,
} from "react-native";
import React, { useEffect, useRef, useState } from "react";
import ProgressBar from "../../components/ProgressBar";
import Colors from "../../constants/Colors";
import { Feather } from "@expo/vector-icons";
import { useForm } from "react-hook-form";
import { getLocalData } from "../../services/storage";
import { useQueryClient } from "react-query";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import QuestionSelect from "../SignIn/QuestionSelect";
import QuestionInput from "../SignIn/QuestionInput";
import QuestionThanks from "../SignIn/QuestionThanks";
import QuestionInfo from "../SignIn/QuestionInfo";
import useCheckInAttendee from "../../hooks/useCheckInAttendee";

interface LiteQRSignInProps {
  questions: any;
  step: number;
  setStep: (step: number) => void;
  change: boolean;
  team?: string;
  phone?: string;
  checkInKey?: string | null;
}

const LiteQRSignIn: React.FC<LiteQRSignInProps> = ({
  questions,
  step,
  setStep,
  change,
  team,
  phone,
  checkInKey,
}) => {
  const totalSteps = 1;
  const { width } = useWindowDimensions();
  const scrollRef = useRef<ScrollView>(null);
  const queryClient = useQueryClient();
  const { submitAttendee, isSubmitting } = useCheckInAttendee(checkInKey);

  const [attendee, setAttendee] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    intent: "",
    collectedForm: {
      collectedQuestionsAndAnswers: {},
    },
  });
  const [disabled, setDisabled] = useState(false);
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address: "",
    },
  });

  useEffect(() => {
    if (step === 0) {
      setDisabled(false);
      checkAttendeeValues();
    } else if (step === totalSteps + 1) {
      setTimeout(() => {
        checkAttendeeValues();
      }, 250);
    }
  }, [step, change]);

  const clearData = () => {
    setValue("first_name", "");
    setValue("last_name", "");
    setValue("email", "");
    setValue("phone", "");
    setValue("address", "");
    setAttendee({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      intent: "",
      collectedForm: {
        collectedQuestionsAndAnswers: {},
      },
    });
  };

  const checkAttendeeValues = () => {
    const { email, phone, first_name, last_name } = getValues();
    if (email && phone && first_name && last_name) {
      saveAttendee({
        first_name,
        last_name,
        email,
        phone,
        intent: attendee?.intent || "",
        collectedForm: {
          collectedQuestionsAndAnswers:
            attendee?.collectedForm.collectedQuestionsAndAnswers || {},
        },
      });
    } else {
      clearData();
    }
  };

  const _nextStep = async () => {
    goToNextStep();
  };

  const saveAttendee = async (_temp: any | null) => {
    if (isSubmitting) return;

    const attendeeToSave = _temp ?? attendee;
    const answers =
      attendeeToSave.collectedForm?.collectedQuestionsAndAnswers ?? {};
    const payload = {
      ...attendeeToSave,
      ...(Object.keys(answers).length > 0
        ? {
            collectedForm: {
              ...attendeeToSave.collectedForm,
              collectedQuestionsAndAnswers: { ...answers },
            },
          }
        : { collectedForm: undefined }),
    };

    setDisabled(true);
    try {
      const result = await submitAttendee(payload);
      if (result.status === "queued") {
        alert(
          "You appear to be offline. Your information was saved on this device and will be submitted automatically when the connection returns."
        );
      } else {
        try {
          await queryClient.invalidateQueries(["getTags"]);
        } catch (error) {
          console.error("Attendee saved, but tag refresh failed:", error);
        }
      }

      clearData();
      setStep(0);
    } catch (error) {
      console.error("Error saving attendee:", error);
      alert(
        "Your information could not be submitted. It is still on this form—please try again or contact the listing agent."
      );
    } finally {
      setDisabled(false);
    }
  };

  const goToNextStep = () => {
    if (step !== 2 && step !== 4) {
      setDisabled(true);
    }
    let nextStep = step + 1;
    scrollRef.current?.scrollTo({ x: nextStep * width, y: 0, animated: true });
    setStep(nextStep);
  };

  const handleSelect = (value: string, question: string) => {
    setAttendee({
      ...attendee,
      collectedForm: {
        collectedQuestionsAndAnswers: {
          ...attendee.collectedForm.collectedQuestionsAndAnswers,
          [value.question_uuid]: [
            {
              questionText: question,
              answerUuid: value.uuid,
              answer: value.label,
            },
          ],
        },
      },
    });
    setDisabled(false);
  };

  const submit = async (data: any) => {
    setAttendee({
      ...attendee,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone,
    });
    goToNextStep();
  };

  const submitFirstStep = () => {
    Keyboard.dismiss();
    void handleSubmit(submit, (validationErrors) => {
      console.warn("Check-in contact validation failed:", validationErrors);
    })();
  };

  const handleNextPress = step === 0 ? submitFirstStep : _nextStep;

  return (
    <View style={styles.body}>
      <View style={styles.container}>
        <ProgressBar step={step + 1} totalSteps={totalSteps + 1} />
      </View>
      <View style={{ width: "100%", maxWidth: 500 }}>
        {step === 0 ? (
          <View style={{ width: "100%", maxWidth: 500 }}>
            <QuestionInfo
              control={control}
              errors={errors}
              question="Please sign in"
              details="Upon the homeowner's request, we ask that all open house attendees sign in."
              team={team}
              phone={phone}
            />
          </View>
        ) : null}
        {questions?.map((question, index) => {
          if (step === 1) {
            if (question.question_type !== 5) {
              return (
                <QuestionSelect
                  key={question.uuid}
                  question={question.question}
                  details={question.details}
                  options={[]}
                  onPress={(value) => {
                    handleSelect(value, question.question);
                  }}
                  uuid={question.uuid}
                  prefetchedAnswers={question?.answers}
                />
              );
            }
          }
        })}
        {step === 2 ? (
          <QuestionThanks question="Thank you for signing in!" details="" />
        ) : null}
      </View>
      <View style={styles.buttonRow} pointerEvents="box-none">
        {step !== totalSteps + 1 && (
          <Pressable
            style={disabled ? styles.disabledButton : styles.button}
            onPress={Platform.OS === "android" ? undefined : handleNextPress}
            onPressIn={
              Platform.OS === "android" ? handleNextPress : undefined
            }
            disabled={disabled}
          >
            <Text style={styles.buttonText}>
              {step === totalSteps ? "Submit" : "Next"}
            </Text>
            {step !== totalSteps ? (
              <Feather name="arrow-right" size={26} color="white" />
            ) : null}
          </Pressable>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
  },
  container: {
    marginTop: 0,
    paddingHorizontal: 20,
    paddingVertical: 14,
    maxWidth: 500,
    width: "100%",
  },
  buttonRow: {
    padding: 20,
    paddingBottom: 0,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    position: "relative",
    zIndex: 10,
    elevation: 10,
  },
  button: {
    backgroundColor: Colors.primary_500,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 40,
    flexDirection: "row",
    alignItems: "center",
  },
  disabledButton: {
    backgroundColor: Colors.nuetral_grey_400,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 40,
    flexDirection: "row",
    alignItems: "center",
  },
  buttonText: {
    color: "white",
    borderRadius: 5,
    fontSize: 26,
    fontFamily: "NunitoSemiBold",
    marginRight: 8,
  },
});

export default LiteQRSignIn;
